import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { RefreshCw, WifiOff, LogOut } from 'lucide-react';
import { acctsAPI, usersAPI } from '../api';
import { Card, Button, PageHeader, Alert, Pagination, Spinner, Modal, useSort } from '../components/UI';
import { useTenant } from '../TenantContext';

const STATUS_OPTS = [
  { value: '',        label: 'Semua'      },
  { value: 'online',  label: '🟢 Online'  },
  { value: 'offline', label: '⚫ Offline'  },
];

const RowMenuButton = ({ row, onDisconnect, onClose }) => {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const h = () => setOpen(false);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [open]);
  return (
    <div>
      <button ref={btnRef}
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '3px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1 }}>
        ···
      </button>
      {open && ReactDOM.createPortal(
        <div onClick={e => e.stopPropagation()} style={{
          position: 'absolute',
          top: (() => { const r = btnRef.current?.getBoundingClientRect(); return r ? r.bottom + window.scrollY + 4 : 0; })(),
          left: (() => { const r = btnRef.current?.getBoundingClientRect(); return r ? r.right + window.scrollX : 0; })(),
          transform: 'translateX(-100%)', zIndex: 9999,
          background: 'var(--bg-secondary)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 160, overflow: 'hidden',
        }}>
          <button onClick={() => { setOpen(false); onDisconnect(); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--accent-orange)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <LogOut size={12} style={{ marginRight: 7, verticalAlign: 'middle' }} />Disconnect
          </button>
          <div style={{ borderTop: '1px solid var(--border)' }} />
          <button onClick={() => { setOpen(false); onClose(); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--accent-red)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            ✕ Close Sesi
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default function OnlineUsers() {
  const { selectedTenant } = useTenant();
  const [accts, setAccts]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(20);
  const [status, setStatus]       = useState('online');
  const [q, setQ]                 = useState('');
  const [nasip, setNasip]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const [selected, setSelected]         = useState([]);   // array of { acct_row }
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState(null); // single row
  const [closing, setClosing] = useState(false);
  const [bulkCloseConfirm, setBulkCloseConfirm] = useState(false);
  const [bulkClosing, setBulkClosing] = useState(false);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const bulkBtnRef = React.useRef(null);

  useEffect(() => {
    if (!bulkMenuOpen) return;
    const handler = () => setBulkMenuOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [bulkMenuOpen]);

  const { sort, onSortChange, sortParam } = useSort();

  const load = useCallback(async () => {
    if (!selectedTenant) { setAccts([]); return; }
    setLoading(true); setError(null);
    try {
      const params = { page, per_page: perPage };
      if (status)    params.status = status;
      if (q)         params.q = q;
      if (nasip)     params.nasipaddress = nasip;
      if (sortParam) params.sort = sortParam;
      const res = await acctsAPI.latest(selectedTenant, params);
      const rows = res._array || res.accts || res.data || (Array.isArray(res) ? res : []);
      setAccts(rows);
      setTotal(res.total || rows.length || 0);
      setTotalPages(res.total_pages || 1);
      setLastRefresh(new Date());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [selectedTenant, page, status, q, nasip, perPage, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected([]); }, [selectedTenant, status, q, nasip]);

  // auto refresh every 30s when online tab
  useEffect(() => {
    if (status !== 'online') return;
    const t = setInterval(() => load(), 30000);
    return () => clearInterval(t);
  }, [load, status]);

  // Disconnect: for each selected username, look up user by username then disconnect
  const handleDisconnect = async () => {
    setDisconnecting(true);
    let ok = 0, fail = 0, errors = [];
    for (const row of selected) {
      const username = row.username;
      try {
        // Try direct user_id if available in acct row
        let uid = row.user_id;
        if (!uid) {
          // Fetch user by username
          const res = await usersAPI.list(selectedTenant, { q: username, per_page: 1 });
          const users = res.users || [];
          const match = users.find(u => u.username === username);
          if (!match) { fail++; errors.push(`${username}: user tidak ditemukan`); continue; }
          uid = match.id;
        }
        await usersAPI.disconnect(selectedTenant, uid);
        ok++;
      } catch (e) {
        fail++;
        errors.push(`${username}: ${e.message}`);
      }
    }
    setDisconnecting(false);
    setDisconnectConfirm(false);
    setSelected([]);
    if (ok > 0) setSuccess(`${ok} user di-disconnect.${fail > 0 ? ` ${fail} gagal.` : ''}`);
    if (fail > 0 && ok === 0) setError(errors.slice(0, 3).join(' | '));
    load();
  };

  const getKey = (a) => a.radacctid ?? a.id ?? a.username;

  const handleForceClose = async () => {
    if (!closeConfirm) return;
    setClosing(true);
    try {
      await acctsAPI.forceClose(selectedTenant, closeConfirm.radacctid);
      setSuccess(`Sesi ${closeConfirm.username} berhasil di-close.`);
      setCloseConfirm(null);
      load();
    } catch (e) { setError(e.message); }
    finally { setClosing(false); }
  };

  const handleBulkClose = async () => {
    setBulkClosing(true);
    let ok = 0, fail = 0;
    for (const row of selected) {
      try {
        await acctsAPI.forceClose(selectedTenant, row.radacctid);
        ok++;
      } catch { fail++; }
    }
    setBulkClosing(false);
    setBulkCloseConfirm(false);
    setSelected([]);
    if (ok > 0) setSuccess(`${ok} sesi di-close.${fail > 0 ? ` ${fail} gagal.` : ''}`);
    if (fail > 0 && ok === 0) setError(`${fail} sesi gagal di-close.`);
    load();
  };
  const allKeys = accts.map(getKey);
  const allChecked = allKeys.length > 0 && allKeys.every(k => selected.find(r => getKey(r) === k));
  const someChecked = !allChecked && allKeys.some(k => selected.find(r => getKey(r) === k));
  const isSelected = (row) => !!selected.find(r => getKey(r) === getKey(row));
  const toggleRow = (row) => {
    if (isSelected(row)) setSelected(s => s.filter(r => getKey(r) !== getKey(row)));
    else setSelected(s => [...s, row]);
  };
  const toggleAll = () => {
    if (allChecked) setSelected([]);
    else setSelected(accts);
  };
  const cbStyle = { width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent-blue)' };

  const inputStyle = {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: '7px 12px',
    color: 'var(--text-primary)', fontSize: 12, outline: 'none',
    fontFamily: 'var(--font-sans)',
  };

  const HIDDEN_COLS = ['acctupdatetime', 'updatetime', 'acct_update_time'];
  const rawCols = accts.length > 0 ? Object.keys(accts[0]).filter(k => !HIDDEN_COLS.includes(k.toLowerCase())) : [];
  // Pastikan status muncul tepat setelah username
  const cols = (() => {
    const rest = rawCols.filter(k => k !== 'username' && k !== 'status');
    const hasUsername = rawCols.includes('username');
    const hasStatus   = rawCols.includes('status');
    return [
      ...(hasUsername ? ['username'] : []),
      ...(hasStatus   ? ['status']   : []),
      ...rest,
    ];
  })();

  const formatVal = (key, val, row = {}) => {
    if (val === null || val === undefined) return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>;
    if (['acctstoptime','acctstarttime'].includes(key))
      return <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{val ? new Date(val).toLocaleString('id-ID', { hour12: false }) : '—'}</span>;
    if (key === 'acctsessiontime' || key === 'duration' || key === 'duration_seconds') {
      const days  = Math.floor(val / 86400);
      const hours = Math.floor((val % 86400) / 3600);
      const mins  = Math.floor((val % 3600) / 60);
      const secs  = Math.floor(val % 60);
      const pad = (n) => String(n).padStart(2, '0');
      const hms = `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
      return <span className="mono" style={{ fontSize: 11 }}>{days > 0 ? `${days}d ${hms}` : hms}</span>;
    }
    if (['acctinputoctets','acctoutputoctets'].includes(key)) {
      const mb = (val/1048576).toFixed(1);
      return <span className="mono" style={{ fontSize: 11, color: 'var(--accent-cyan)' }}>{mb} MB</span>;
    }
    if (['framedipaddress','nasipaddress','calledstationid','callingstationid'].includes(key))
      return <span className="mono" style={{ fontSize: 11, color: 'var(--text-mono)' }}>{val}</span>;
    if (key === 'username')
      return <span style={{ fontWeight: 500, fontSize: 12 }}>{val}</span>;
    if (key === 'status') {
      const isOnline = val === 'online';
      return (
        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600, whiteSpace: 'nowrap',
          background: isOnline ? 'rgba(16,185,129,0.12)' : 'rgba(100,100,100,0.1)',
          color:      isOnline ? 'var(--accent-green)'   : 'var(--text-muted)',
          border:     `1px solid ${isOnline ? 'rgba(16,185,129,0.3)' : 'var(--border)'}`,
        }}>
          {isOnline ? '● Online' : '○ Offline'}
        </span>
      );
    }
    if (key === 'acctstatus' || key === 'acctstatustype') {
      const online = String(val).toLowerCase() === 'start' || String(val).toLowerCase() === 'alive';
      return <span style={{ fontSize: 11, padding: '2px 8px', background: online ? 'rgba(16,185,129,0.12)' : 'var(--bg-input)', color: online ? 'var(--accent-green)' : 'var(--text-muted)', borderRadius: 20, border: `1px solid ${online ? 'rgba(16,185,129,0.3)' : 'var(--border)'}` }}>{val}</span>;
    }
    return <span style={{ fontSize: 12 }}>{String(val)}</span>;
  };

  return (
    <div className="animate-in">
      <PageHeader
        title="Online Users"
        subtitle={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {`${total} session`}
            {lastRefresh && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· refresh {lastRefresh.toLocaleTimeString('id-ID', { hour12: false })}</span>}
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            {selected.length > 0 && (<>
              <Button variant="ghost" onClick={() => setDisconnectConfirm(true)} style={{ color: 'var(--accent-orange)', borderColor: 'var(--accent-orange)' }}>
                <LogOut size={13} /> Disconnect {selected.length}
              </Button>
              <Button variant="danger" onClick={() => setBulkCloseConfirm(true)}>
                ✕ Close Sesi {selected.length}
              </Button>
            </>)}
            <Button variant="ghost" onClick={load} loading={loading}>
              <RefreshCw size={13} /> Refresh
            </Button>
          </div>
        }
      />

      {error   && <Alert type="error"   message={error}   onClose={() => setError(null)}   />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      {/* Filters */}
      <div className="filter-bar" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 4 }}>
          {STATUS_OPTS.map(opt => (
            <button key={opt.value} onClick={() => setStatus(opt.value)} style={{
              padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              background: status === opt.value ? 'rgba(59,130,246,0.15)' : 'transparent',
              color:      status === opt.value ? 'var(--accent-blue)' : 'var(--text-muted)',
              transition: 'all 0.15s',
            }}>{opt.label}</button>
          ))}
        </div>
        <input style={{ ...inputStyle, width: 180 }} placeholder="Cari username / IP…" value={q} onChange={e => setQ(e.target.value)} />
        <input style={{ ...inputStyle, width: 160 }} placeholder="NAS IP address…" value={nasip} onChange={e => setNasip(e.target.value)} />
      </div>

      <Card style={{ padding: 0 }}>
        {loading && accts.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner size={28} /></div>
        ) : !selectedTenant ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--text-muted)', fontSize: 13 }}>Pilih tenant terlebih dahulu</div>
        ) : accts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
            <WifiOff size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
            <div>Tidak ada session {status ? status : ''}</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '8px 12px', width: 36 }}>
                    <input type="checkbox" style={cbStyle} checked={allChecked}
                      ref={el => { if (el) el.indeterminate = someChecked; }}
                      onChange={toggleAll} />
                  </th>
                  {cols.map(col => {
                    const active = sort?.key === col;
                    return (
                      <th key={col} style={{ padding: '8px 12px', textAlign: 'left', color: active ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                        <button onClick={() => onSortChange(col)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, font: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}>
                          {col.replace(/^acct/i,'').replace(/([A-Z])/g,' $1').trim() || col}
                          <span style={{ fontSize: 9, opacity: active ? 1 : 0.35 }}>{active ? (sort.dir === 'desc' ? '▼' : '▲') : '⇅'}</span>
                        </button>
                      </th>
                    );
                  })}
                  <th style={{ padding: '8px 12px' }} />
                </tr>
              </thead>
              <tbody>
                {accts.map((row, i) => {
                  const key = getKey(row);
                  const sel = isSelected(row);
                  return (
                    <tr key={key ?? i}
                      style={{ borderBottom: '1px solid var(--border)', background: sel ? 'rgba(59,130,246,0.07)' : '' }}
                      onMouseEnter={e => { if (!sel) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = sel ? 'rgba(59,130,246,0.07)' : ''; }}>
                      <td style={{ padding: '8px 12px' }}>
                        {row.status === 'online'
                          ? <input type="checkbox" style={cbStyle} checked={sel} onChange={() => toggleRow(row)} />
                          : <span style={{ display: 'inline-block', width: 16 }} />
                        }
                      </td>
                      {cols.map(col => (
                        <td key={col} style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                          {formatVal(col, row[col], row)}
                        </td>
                      ))}
                      <td style={{ padding: '8px 12px' }}>
                        {row.status === 'online' && (
                          <RowMenuButton row={row} onDisconnect={() => { setSelected([row]); setDisconnectConfirm(true); }} onClose={() => setCloseConfirm(row)} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ padding: '0 16px' }}>
          <Pagination page={page} totalPages={totalPages} total={total} onChange={p => { setPage(p); setSelected([]); }} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); setSelected([]); }} />
        </div>
      </Card>

      {/* Disconnect Confirm */}
      <Modal open={disconnectConfirm} onClose={() => setDisconnectConfirm(false)} title="Disconnect User" width={400}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
          Yakin ingin disconnect <strong style={{ color: 'var(--accent-red)' }}>{selected.length} user</strong> berikut?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 20, maxHeight: 160, overflowY: 'auto' }}>
          {selected.map(row => (
            <div key={getKey(row)} style={{ fontSize: 12, padding: '4px 10px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', color: 'var(--text-secondary)' }}>
              <span className="mono" style={{ color: 'var(--text-mono)' }}>{row.username}</span>
              {row.framedipaddress && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{row.framedipaddress}</span>}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDisconnectConfirm(false)}>Batal</Button>
          <Button variant="danger" loading={disconnecting} onClick={handleDisconnect}>
            <LogOut size={13} /> Disconnect
          </Button>
        </div>
      </Modal>

      {/* Bulk Close Sesi Confirm */}
      <Modal open={bulkCloseConfirm} onClose={() => setBulkCloseConfirm(false)} title="Close Sesi Bulk" width={400}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
          Yakin ingin close sesi <strong style={{ color: 'var(--accent-red)' }}>{selected.length} baris</strong> berikut?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 20, maxHeight: 160, overflowY: 'auto' }}>
          {selected.map(row => (
            <div key={getKey(row)} style={{ fontSize: 12, padding: '4px 10px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
              <span className="mono" style={{ color: 'var(--text-mono)' }}>{row.username}</span>
              <span style={{ color: 'var(--text-muted)' }}>#{row.radacctid}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setBulkCloseConfirm(false)}>Batal</Button>
          <Button variant="danger" loading={bulkClosing} onClick={handleBulkClose}>✕ Close Sesi</Button>
        </div>
      </Modal>

      {/* Close Sesi single */}
      <Modal open={!!closeConfirm} onClose={() => setCloseConfirm(null)} title="Close Sesi" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
          Yakin ingin close sesi untuk user{' '}
          <strong style={{ color: 'var(--accent-red)' }}>{closeConfirm?.username}</strong>?
          {closeConfirm?.radacctid && <span style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginTop: 4 }}>radacctid: {closeConfirm.radacctid}</span>}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setCloseConfirm(null)}>Batal</Button>
          <Button variant="danger" loading={closing} onClick={handleForceClose}>
            ✕ Close Sesi
          </Button>
        </div>
      </Modal>
    </div>
  );
}